import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { clientAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, Mail, Key, LogIn, Shield, FileText } from 'lucide-react';
import { toast } from 'sonner';

export default function ClientPortalLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !accessCode) {
      toast.error('Please enter your email and access code');
      return;
    }

    setLoading(true);
    try {
      const caseId = searchParams.get('case');
      const response = await clientAPI.login(email, accessCode, caseId);
      
      // Store client token
      localStorage.setItem('client_token', response.data.access_token);
      localStorage.setItem('client_data', JSON.stringify(response.data.client));
      
      toast.success('Welcome to your client portal!');
      // Pass through query params (e.g., tab=invoices from email links)
      const tab = searchParams.get('tab');
      navigate(`/client-portal/dashboard${tab ? `?tab=${tab}` : ''}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img src="/logo.png" alt="NewSlate" className="h-14 object-contain" />
          <span className="font-body text-xs text-accent uppercase tracking-wider">Client Portal</span>
        </div>

        <Card className="shadow-lg border-0" data-testid="client-login-card">
          <CardHeader className="text-center pb-4">
            <CardTitle className="font-heading text-xl text-foreground">
              Access Your Case
            </CardTitle>
            <CardDescription className="font-body text-muted-foreground">
              Enter your email and the 6-digit access code provided by your case manager
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4" data-testid="client-login-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-body text-sm font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 font-body"
                    data-testid="client-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="accessCode" className="font-body text-sm font-medium">
                  Access Code
                </Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="accessCode"
                    type="text"
                    placeholder="123456"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="pl-10 font-body text-lg tracking-widest"
                    maxLength={6}
                    data-testid="client-access-code-input"
                  />
                </div>
                <p className="text-xs text-muted-foreground font-body">
                  6-digit code provided by your case manager
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full btn-lift bg-primary text-white font-body"
                disabled={loading}
                data-testid="client-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4 mr-2" />
                    Access My Portal
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Features Preview */}
        <div className="mt-8 grid grid-cols-2 gap-4">
          <div className="p-4 bg-card rounded-lg border border-border">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
              <FileText className="w-5 h-5 text-primary" />
            </div>
            <h3 className="font-heading text-sm font-semibold text-foreground">View Case Status</h3>
            <p className="font-body text-xs text-muted-foreground mt-1">
              Track your case progress in real-time
            </p>
          </div>
          <div className="p-4 bg-card rounded-lg border border-border">
            <div className="w-10 h-10 rounded-lg bg-accent/20 flex items-center justify-center mb-3">
              <Shield className="w-5 h-5 text-accent" />
            </div>
            <h3 className="font-heading text-sm font-semibold text-foreground">Submit Documents</h3>
            <p className="font-body text-xs text-muted-foreground mt-1">
              Securely upload required documents
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-8 font-body">
          Don't have an access code? Contact your case manager.
        </p>
      </div>
    </div>
  );
}
